import { config, BASE_NODE_GAP, BASE_LEVEL_HEIGHT } from './config.js';
import { labelMaxCharsAt } from './labelText.js';
import {
  getName,
  getAcronym,
  collectPhenotypeSource,
  collectGenotypeSource,
  getNodeBlockHeight
} from './utils.js';
import { getGenotypeDisplayParts } from 'src/util/util';
import {
  STACKED_LABEL_BLOCKS,
  labelBlockItems,
  blockEnabled,
  buildTestItems
} from './labelText.js';

// ─── Auto-spacing ────────────────────────────────────────────────────────────
// Derive the GLOBAL node spacing (config.NODE_GAP / config.LEVEL_HEIGHT) from the
// labels each node will actually render, so a label-heavy pedigree spaces itself
// instead of the user hand-tuning the Spacing fields. Pure + deterministic given
// (members, options, measure) — `measure` is injected so jsdom unit tests pass a
// stub (no real canvas measureText in jsdom).
//
// Two passes break the wrap↔width circular dependency (labelMaxChars depends on
// NODE_GAP, which we are computing):
//   Pass A — width → NODE_GAP. Single-line "must fit" labels (name, ids,
//     diagnoses, phenotypes, genotypes — the renderer never wraps these) drive
//     the gap UNCAPPED; wrappable free-text blocks (Tests / Diagnosis / Test
//     Comment / Consultation Date / personNote) are CAPPED, since they are meant
//     to wrap into extra height rather than force a giant gap.
//   Pass B — line count → LEVEL_HEIGHT. At the chosen gap, reserve the exact
//     label-block height via getNodeBlockHeight (the same accumulation the
//     renderer/getLabelPos use), so measured == rendered.
// Both axes floor at BASE, so auto never makes a chart tighter than today's
// default → it can only ever space OUT, never introduce a new overlap.
// ──────────────────────────────────────────────────────────────────────────────

// Tunable constants. The K caps match the manual Spacing control's 500% ceiling
// (CanvasPreferences max:500), so auto is never LESS capable than manual — in
// particular the VERTICAL cap must reach what manual could, or enabling auto on
// a very tall, label-dense node could leave labels overlapping the generation
// below where a manual 400–500% setting would not.
const K_H = 5; // max NODE_GAP = K_H * BASE_NODE_GAP (= horizontal_spacing 500%)
const K_V = 5; // max LEVEL_HEIGHT = K_V * BASE_LEVEL_HEIGHT (= vertical 500%)
const H_MARGIN_SYM = 1.0; // horizontal breathing room = this * SYMBOL_SIZE
const FREE_TEXT_WIDTH_CAP = 1.6 * BASE_NODE_GAP; // wrappable text wraps past this
const V_MARGIN_FS = 1.0; // vertical bottom margin = this * font_size
const NEXT_NODE_CLEARANCE_SYM = 0.8; // room for the next generation's symbol top
// childPath draws the horizontal sibship bar (and the vertical drops from it to
// each child) at the MIDPOINT of the inter-generation gap (midY = (uy+cy)/2).
// A parent's label block hangs straight down from the parent row, so for the
// block to clear that bar/drops the gap must be ~2× the block depth: a parent
// node with tall labels needs LEVEL_HEIGHT >= blockBottom / BAR_GAP_FRACTION.
const BAR_GAP_FRACTION = 0.5;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Round to the nearest whole percent for the read-only Spacing display so it
// faithfully reflects the px actually applied (the chart uses the raw px).
const roundPct = (n) => Math.round(n);

// Symbol size to compute against: prefer the (possibly unsaved) value from the
// settings form so the modal preview tracks symbol-size edits; fall back to the
// live applied singleton (the render path passes options carrying the saved
// value, so both agree there).
const symbolSizeOf = (options) =>
  Number(options?.symbol_size) || Number(config.SYMBOL_SIZE) || 155;

// Offscreen-canvas text measurer, created once. Caches by `${font} ${text}`.
// Returns 0 on any failure (jsdom / no canvas) — callers inject a stub in tests.
export function makeTextMeasurer() {
  let ctx = null;
  let lastFont = null;
  const cache = new Map();
  try {
    ctx = document.createElement('canvas').getContext('2d');
  } catch (_e) {
    ctx = null;
  }
  return function measure(text, font) {
    const str = String(text ?? '');
    if (!str) return 0;
    if (!ctx) return 0;
    const key = `${font} ${str}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    if (font !== lastFont) {
      ctx.font = font;
      lastFont = font;
    }
    const w = ctx.measureText(str).width || 0;
    cache.set(key, w);
    return w;
  };
}

// Default singleton measurer for the live app.
export const measureTextWidth = makeTextMeasurer();

const labelFont = (options) =>
  `${
    Number(options?.font_size) || 16
  }px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif`;

// The unwrapped label strings a node will render, split by how the renderer
// treats them: `singleLine` (never wrapped → must fit the gap) vs `wrappable`
// (wrapped into lines → width-capped). Mirrors the renderer's text construction
// so the widths track what's drawn.
export function getNodeUnwrappedLines(d, options) {
  const ls = options?.labelsSet || {};
  const singleLine = [];
  const wrappable = [];
  const pushLine = (arr, s) => {
    const t = String(s ?? '').trim();
    if (t) arr.push(t);
  };

  // A de-identified (isHide) node still draws its symbol, acronym, and
  // diagnoses/phenotypes/genotypes, but the renderer BLANKS its name / UPN /
  // dates / hospital number / external IDs / personNote and the stacked blocks
  // (Canvas.jsx guards each of those on `d.isHide`). Mirror that here so we
  // don't reserve width for labels that render empty.
  const deidentified = !!d.isHide;

  pushLine(singleLine, getAcronym(d));
  if (!deidentified) {
    if ((ls.firstName && d.firstName) || (ls.lastName && d.lastName)) {
      pushLine(singleLine, getName({ data: d, options }));
    }
    if (ls.upn && (d.proBandId || d.upn)) {
      pushLine(singleLine, d.proBandId ? '1' : d.upn);
    }
    if (ls.dob && d.dob) pushLine(singleLine, `DoB: ${d.dob}`);
    if (ls.age && d.dob) pushLine(singleLine, `Age 00`);
    if (ls.dod && d.dod) pushLine(singleLine, `DoD: ${d.dod}`);
    if (ls.hospitalNumber && d.hospitalNumber) {
      pushLine(singleLine, String(d.hospitalNumber));
    }
    if (ls.externalPatientId && Array.isArray(d.externalPatientIds)) {
      d.externalPatientIds.forEach((id) => pushLine(singleLine, id));
    }
  }

  // Diagnoses (with age) — one line per diagnosis, not wrapped by the renderer.
  if (ls.diseaseWithAge && Array.isArray(d.diagnoses)) {
    d.diagnoses
      .filter(
        (di) =>
          di.displayOnPedigree !== false &&
          (!ls.diseasesMap || ls.diseasesMap[di.id])
      )
      .forEach((di) =>
        pushLine(
          singleLine,
          `${di.alias || di.name || ''}${di.age ? ` (${di.age})` : ''}`
        )
      );
  }

  // Phenotypes — name and/or (id).
  if (ls.phenotypesName || ls.phenotypesID) {
    collectPhenotypeSource(d)
      .filter(
        (p) =>
          p.displayOnPedigree !== false &&
          (!ls.phenotypesMap || ls.phenotypesMap[p.id])
      )
      .forEach((p) => {
        const namePart = ls.phenotypesName ? p.name : '';
        const idPart = ls.phenotypesID ? `(${p.id})` : '';
        // The renderer appends " - Absent" for an unobserved phenotype; include
        // it so the measured width matches what's drawn.
        const absent = p.observed === false ? ' - Absent' : '';
        pushLine(
          singleLine,
          `${namePart}${namePart && idPart ? ' ' : ''}${idPart}${absent}`
        );
      });
  }

  // Genotypes — symbol and/or (id).
  if (ls.genotypesName || ls.genotypesID) {
    collectGenotypeSource(d)
      .filter(
        (g) =>
          g.displayOnPedigree !== false &&
          (!ls.genotypesMap || ls.genotypesMap[g.id])
      )
      .forEach((g) => {
        const parts = getGenotypeDisplayParts(g);
        const namePart = ls.genotypesName && parts.symbol ? parts.symbol : '';
        const idPart = ls.genotypesID ? `(${g.id})` : '';
        pushLine(
          singleLine,
          `${namePart}${namePart && idPart ? ' ' : ''}${idPart}`
        );
      });
  }

  // Wrappable stacked blocks (header is short; items are the free text). The
  // renderer blanks these on de-identified nodes (Canvas `!d.isHide` filter),
  // so skip them here too — otherwise we'd reserve gap width for text that node
  // never draws.
  if (!deidentified) {
    for (const blk of STACKED_LABEL_BLOCKS) {
      if (!blockEnabled(blk, ls)) continue;
      if (blk.key === 'tests') {
        // Composed Tests block: "Tests:" header, then each test's "<name> (...)"
        // main line and its comment — measured unwrapped like other free text
        // so the width cap / height growth tracks what's drawn.
        const items = buildTestItems(d, ls);
        if (!items.length) continue;
        pushLine(wrappable, 'Tests:');
        items.forEach((it) => {
          pushLine(wrappable, it.main);
          pushLine(wrappable, it.comment);
        });
      } else {
        const items = labelBlockItems(d, blk.field);
        if (!items.length) continue;
        pushLine(wrappable, blk.header);
        items.forEach((it) => pushLine(wrappable, it));
      }
    }
  }
  if (ls.personNote && d.personNote) pushLine(wrappable, d.personNote);

  return { singleLine, wrappable };
}

// Pass A: the global NODE_GAP needed so adjacent (centred) labels don't overlap.
export function computeTargetNodeGap(members, options, measure) {
  const font = labelFont(options);
  const sym = symbolSizeOf(options);
  let maxWidth = 0;
  for (const d of members || []) {
    if (!d || d.hidden) continue;
    const { singleLine, wrappable } = getNodeUnwrappedLines(d, options);
    let nodeWidth = 0;
    for (const s of singleLine)
      nodeWidth = Math.max(nodeWidth, measure(s, font));
    for (const s of wrappable) {
      nodeWidth = Math.max(
        nodeWidth,
        Math.min(measure(s, font), FREE_TEXT_WIDTH_CAP)
      );
    }
    maxWidth = Math.max(maxWidth, nodeWidth);
  }
  return clamp(
    maxWidth + H_MARGIN_SYM * sym,
    BASE_NODE_GAP,
    K_H * BASE_NODE_GAP
  );
}

// Whether a member has descendants — i.e. a child-connector (sibship bar +
// drops) is drawn in the gap directly below it. Such a node's label block must
// clear the mid-gap bar; a leaf's need only clear the next generation's nodes.
function buildHasChildren(members) {
  const parentPids = new Set();
  for (const m of members || []) {
    if (m?.fatherId != null) parentPids.add(String(m.fatherId));
    if (m?.motherId != null) parentPids.add(String(m.motherId));
  }
  return (m) =>
    (Array.isArray(m?.childrenIds) && m.childrenIds.length > 0) ||
    (m?.pid != null && parentPids.has(String(m.pid)));
}

// Pass B: tallest label-block at the chosen gap. Returns the global max (every
// node's labels must clear the next generation's NODES) and the max over nodes
// WITH children (those also have a sibship bar at the gap midpoint to clear).
export function computeMaxBlockHeight(members, options, nodeGapPx) {
  const sym = symbolSizeOf(options);
  const maxChars = labelMaxCharsAt(nodeGapPx, sym, options?.font_size);
  const hasChildren = buildHasChildren(members);
  let maxBottom = 0;
  let maxBottomWithChildren = 0;
  for (const d of members || []) {
    if (!d || d.hidden) continue;
    const bottom = getNodeBlockHeight(d, options, maxChars, sym);
    maxBottom = Math.max(maxBottom, bottom);
    if (hasChildren(d)) {
      maxBottomWithChildren = Math.max(maxBottomWithChildren, bottom);
    }
  }
  return { maxBottom, maxBottomWithChildren };
}

// Main entry. Returns the derived pixel gaps (set straight onto config) plus the
// equivalent percentages (for the read-only Spacing fields in the settings UI).
export function computeAutoSpacing(
  members,
  options,
  measure = measureTextWidth
) {
  const sym = symbolSizeOf(options);
  const fs = Number(options?.font_size) || 16;

  const nodeGapPx = computeTargetNodeGap(members, options, measure);
  const { maxBottom, maxBottomWithChildren } = computeMaxBlockHeight(
    members,
    options,
    nodeGapPx
  );
  // (a) every node's labels clear the NEXT generation's nodes;
  // (b) a node WITH children must also clear the sibship bar/drops at the gap
  //     midpoint, so the gap is ~block / BAR_GAP_FRACTION (≈ 2× the block).
  const levelForNextNode =
    maxBottom + NEXT_NODE_CLEARANCE_SYM * sym + V_MARGIN_FS * fs;
  const levelForSibBar =
    maxBottomWithChildren > 0
      ? (maxBottomWithChildren + V_MARGIN_FS * fs) / BAR_GAP_FRACTION
      : 0;
  const levelHeightPx = clamp(
    Math.max(levelForNextNode, levelForSibBar),
    BASE_LEVEL_HEIGHT,
    K_V * BASE_LEVEL_HEIGHT
  );

  return {
    nodeGapPx,
    levelHeightPx,
    horizontalSpacingPct: roundPct((nodeGapPx / BASE_NODE_GAP) * 100),
    verticalSpacingPct: roundPct((levelHeightPx / BASE_LEVEL_HEIGHT) * 100)
  };
}
